<template>
  <section class="section is-marginless">
    <div class="columns is-centered">
      <div class="column is-half">
        <b-message v-if="error_message" type="is-danger">
          {{ error_message }}
        </b-message>
        <h3 class="title">Sign up to drive for {{ company.name }}</h3>
        <h3 class="subtitle">Please fill out the following information</h3>
      </div>
    </div>

    <div class="columns is-centered">
      <div class="column is-half">
        <b-field label="Name">
          <b-input size="is-medium" placeholder="First name" v-model="driver.first_name"></b-input>
        </b-field>
        <b-field>
          <b-input size="is-medium" placeholder="Last name" v-model="driver.last_name"></b-input>
        </b-field>
      </div>
    </div>

    <div class="columns is-centered">
      <div class="column is-half full-address">
        <b-field label="Full Address">
          <vue-autosuggest
            :suggestions="[{ data: autoCompletes.map(item => item.description) }]"
            :input-props="{ placeholder:'123 Some Street' }"
            @input="onAddressChange"
            @selected="onSelectAddress"
          >  
            <template v-if="autoCompletes.length" slot-scope="{suggestion}">
              <span class="suggestion-item">{{suggestion.item}}</span>
            </template>
          </vue-autosuggest>
        </b-field>
      </div>
    </div>

    <div class="columns is-centered">
      <div class="column is-half">
        <label class="label">Phone</label>
        <b-field>
          <PhoneCountryPicker ref="phone_country_picker"></PhoneCountryPicker>
          <b-input size="is-medium" placeholder="123-457-7890" v-model="driver.primary_phone" expanded></b-input>
        </b-field>
      </div>
    </div>

    <div class="columns is-centered">
      <div class="column is-half">
        <b-field label="Email address">
          <b-input size="is-medium" placeholder="name@email.com" v-model="driver.email"></b-input>
        </b-field>
      </div>
    </div>

    <div class="columns is-centered">
      <div class="column is-half">
        <b-field label="Login Credentials">
          <b-field>
            <b-input size="is-medium" placeholder="Username" expanded v-model="driver.username"></b-input>
            <p class="control">
              <span class="button is-static is-medium">@{{ company.company_subdomain }}</span>
            </p>
          </b-field>
        </b-field>
        <b-field>
          <b-input size="is-medium" placeholder="Password" v-model="driver.password" type="password" autocomplete="off"></b-input>
        </b-field>
        <b-field>
          <b-input size="is-medium" placeholder="Confirm Password" v-model="password_confirmation" type="password"></b-input>
        </b-field>
      </div>
    </div>

    <div class="columns is-centered">
      <div class="column is-half">
        <h3 class="subtitle">Drivers License</h3>
      </div>
    </div>

    <div class="columns is-centered">
      <div class="column is-half">
        <b-field label="Drivers License Number">
          <b-input size="is-medium" v-model="driver.driver_license_number"></b-input>
        </b-field>
      </div>
    </div>

    <div class="columns is-centered">
      <div class="column is-half">
        <b-field label="Driver License Expiration">
          <b-datepicker size="is-medium" placeholder="Type or select a date..." icon="calendar" icon-pack="fa" v-model="driver.driver_license_expiration"></b-datepicker>
        </b-field>
      </div>
    </div>

    <div class="columns is-centered">
      <div class="column is-half">
        <b-field label="License Class">
          <b-input size="is-medium" v-model="driver.driver_license_class"></b-input>
        </b-field>
      </div>
    </div>

    <div class="columns is-centered">
      <div class="column is-half">
        <h3 class="subtitle">Vehicle Details</h3>
      </div>
    </div>

    <div class="columns is-centered">
      <div class="column is-half">
        <b-field label="Type">
          <b-select placeholder="Select vehicle type" expanded v-model="driver.car_type_id" size="is-medium">
            <option
              v-for="car in car_types"
              :key="car.type_id"
              :value="car.type_id"
            >
              {{ car.name }}
            </option>
          </b-select>
        </b-field>
      </div>
    </div>

    <div class="columns is-centered">
      <div class="column is-half">
        <b-field label="Color">
          <b-input size="is-medium" v-model="driver.car_color"></b-input>
        </b-field>
      </div>
    </div>

    <div class="columns is-centered">
      <div class="column is-half">
        <b-field label="Year">
          <b-input size="is-medium" v-model="driver.car_year"></b-input>
        </b-field>
      </div>
    </div>

    <div class="columns is-centered">
      <div class="column is-half">
        <b-field label="Make">
          <b-input size="is-medium" v-model="driver.car_make"></b-input>
        </b-field>
      </div>
    </div>

    <div class="columns is-centered">
      <div class="column is-half">
        <b-field label="Model">
          <b-input size="is-medium" v-model="driver.car_model"></b-input>
        </b-field>
      </div>
    </div>

    <div class="columns is-centered">
      <div class="column is-half">
        <a class="button is-outlined is-medium is-primary is-fullwidth" @click="save" v-bind:class="{ 'is-loading': is_loading }" v-bind:dir="is_loading">Apply Now</a>
      </div>
    </div>

  </section>
</template>

<script>

  import DriverService from './service'
  import LocationService from '../location/service'
  import { ToastProgrammatic as Toast } from 'buefy'
  import PhoneCountryPicker from '@/components/common/PhoneCountryPicker'
  import { VueAutosuggest } from 'vue-autosuggest';
  import _ from 'lodash';

  export default {

    name: 'DriverSignup',

    components: {
      PhoneCountryPicker,
      VueAutosuggest
    },

    data () {
      return {
        error_message: null,
        driver: {
          first_name: null,
          last_name: null,
          username: null,
          email: null,
          employee_address: {
            address: null
          },
          primary_phone: null,
          primary_phone_country: null,
          password: null,
          driver_license_number: null,
          driver_license_expiration: null,
          driver_license_class: null,
          car_type_id: null,
          car_color: null,
          car_year: null,
          car_make: null,
          car_model: null,
          registration_owner_name: 'na',
          license_plate_number: 'na',
          registration_number: 'na',
          registration_expiration: 'na',
          insurance_provider_name: 'na',
          insurance_policy_number: 'na',
          insurance_expiration_date: 'na'
        },
        company: {
          name: this.$route.query.company_name,
          company_id: this.$route.query.company_id,
          company_subdomain: this.$route.query.company_subdomain
        },
        car_types: [],
        is_loading: false,
        password_confirmation: null,
        autoCompletes: []
      }
    },

    mounted(){

      if (!this.company.name || !this.company.company_id || !this.company.company_subdomain){
        this.error_message = 'Sorry, this company is not configured correctly. Please contact them directly to sign up';
      }

      if (this.$route.query.car_types){
        this.car_types = JSON.parse(this.$route.query.car_types)
      }

    },

    methods: {
      onAddressChange: _.debounce(async function(value = '') {
        this.driver.employee_address.address = value;
        if (!value) {
          this.autoCompletes = [];
          return;
        }
        try {
          const response = await LocationService.getAutoComplete(value);
          this.autoCompletes = response.predictions;
        } catch (err) {
          console.log("error", err);
          this.autoCompletes = [];
        }
      }, 300),

      onSelectAddress(value) {
        this.driver.employee_address.address = value.item;
      },

      save(){

        this.is_loading = true;

        let phone_country = this.$refs.phone_country_picker.selected_country;
        if (phone_country){
          this.driver.primary_phone_country = phone_country.code;
        }

        if (this.driver.password !== this.password_confirmation){
          this.is_loading = false;
          Toast.open({type: 'is-danger', message: 'Passwords do not match, please re-enter your password and confirmation'})
          return;
        }
        console.log('driver', this.driver);

        DriverService.createDriver(this.driver, (err, driver) => {

          this.is_loading = false;

          if (err){
            Toast.open({type: 'is-danger', message: err.message})
            return;
          }

          console.log("Created driver", driver);

          Toast.open({type: 'is-success', message: `Signup successful`})
          this.$router.push({name: 'DriverSignupSuccess'})

        })

      }

    }
  }

</script>
<style lang="css">
  .full-address {
    position: relative;
    height: 90px;
  }

  #autosuggest {
    position: absolute;
    background: white;
    z-index: 100;
    width: calc(100% - 23px);
  }

  #autosuggest input {
    color: #363636;
    font-size: 1.25rem;
    height: 2.25em;
    border-radius: .4em;
    border: 1px solid #dee2e5;
    padding: calc(.375em - 1px) calc(.625em - 1px);
    width: 100%;
    outline: none;
  }

  #autosuggest input:focus {
    border-color: #1abc9c;
    box-shadow: 0 0 0 0.125em rgb(26 188 156 / 25%);
  }

  .autosuggest__results {
    position: absolute;
    background: white;
    z-index: 100;
    width: 100%;
    border: 1px solid #dee2e5;
    border-radius: 4px;
    margin-top: 2px;
    padding: 2px 0;
  }

  .suggestion-item {
    padding: 3px 10px;
    width: 100%;
    display: inline-block;
  }

  .suggestion-item:hover {
    background: #f1f1f1;
    cursor: pointer;
  }
</style>



// WEBPACK FOOTER //
// src/components/driver/DriverSignup.vue